<template>
    <div
        v-show="activeType && activeType === type"
        class="product-type-buttons"
        :class="{ disabled: splitReceipt.active }"
    >
        <div class="category-container">

            <table v-if="grid">
                <tr
                    v-for="(rowBtns, rowIndex) in gridButtons.categories"
                    :key="rowIndex"
                >
                    <td
                        v-for="(button, colIndex) in rowBtns"
                        :id="`${rowIndex}-${colIndex}-categorycontainer`"
                        :key="colIndex + 1000"
                        :style="{ height: (100 / grid.categories_row_count) + '%' }"
                        class="drop-container-category"
                    >
                        <sales-button
                            v-if="button"
                            :id="`${rowIndex}-${colIndex}-categorybtn`"
                            class="category"
                            :button-config="button"
                            :disabled="disabled"
                            @click="selectButton"
                        />
                    </td>
                </tr>
            </table>

        </div>

        <div class="product-container">

            <table v-if="grids.length">
                <tr
                    v-for="(rowBtns, rowIndex) in gridButtons.products"
                    :key="rowIndex"
                >
                    <td
                        v-for="(button, colIndex) in rowBtns"
                        :id="`${rowBtns}-${colIndex}-productcontainer`"
                        :key="colIndex + 1000"
                        :style="{height: (100 / grid.products_row_count) + '%'}"
                        class="drop-container-product"
                    >
                        <sales-button
                            v-if="button"
                            :id="`${rowBtns}-${colIndex}-productbtn`"
                            class="product"
                            :button-config="button"
                            :badge="button.selectedCount"
                            :disabled="disabled"
                            @click="selectButton"
                        />
                    </td>
                </tr>
            </table>

        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Grid from '@/models/Grid';
import Button from '@/models/Button';
import SalesButton from '@/components/SalesButton';

export default {
    components: {
        SalesButton
    },

    props: {
        type: {
            type: String,
            default: null
        },
        activeType: {
            type: String,
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        },
    },

    created() {
        const types = this.$store.state.appConfig.product.types
        this.grid = this.grids[types.indexOf(this.type)]

        // activated
        this.genCategoryButtons()
        this.genProductButtons()
    },

    data: () => ({
        activeCategory: null,
        grid: null,
        gridButtons: {
            categories: [],
            products: [],
        },
    }),

    computed: {
        ...mapGetters(['curOrder', 'splitReceipt']),

        buttons() {
            return Button.query()
                .with('product.tax_rate')
                .with('category')
                .all();
        },

        grids() {
            return Grid.all();
        },
    },

    methods: {
        genCategoryButtons() {
            this.gridButtons.categories = []

            for(let row = 0; row < this.grid.categories_row_count; row++) {
                const rowCategories = []

                for(let column = 0; column < this.grid.categories_column_count; column++) {
                    const button = this.getCategoryButton(row, column)

                    if(button) {
                        rowCategories.push(button)
                    } else {
                        rowCategories.push(null)
                    }
                }

                this.gridButtons.categories.push(rowCategories)
            }
        },

        genProductButtons() {
            this.gridButtons.products = []

            for(let row = 0; row < this.grid.products_row_count; row++) {
                const rowProducts = []

                for(let column = 0; column < this.grid.products_column_count; column++) {
                    const button = this.getProductButton(row, column)

                    if(button) {
                        button.selectedCount = 0
                        rowProducts.push(button)
                    } else {
                        rowProducts.push(null)
                    }
                }

                this.gridButtons.products.push(rowProducts)
            }
        },

        /**
         * Get Category Button
         */
        getCategoryButton(row, column) {
            return this.buttons.find(btn => {
                return btn.type == this.type &&
                    btn.row_num == row + 1 &&
                    btn.column_num == column + 1 &&
                    !btn.product
            })
        },

        /**
         * Get Product Button
         */
        getProductButton(row, column) {
            if (!this.activeCategory) {
                return null;
            }

            return this.buttons.find(btn => {
                return btn.type == this.type &&
                    btn.row_num == row + 1 &&
                    btn.column_num == column + 1 &&
                    btn.category_id == this.activeCategory.id &&
                    !!btn.product
            })
        },

        /**
         * Select Product or Category button
         */
        selectButton(button) {
            if(button.product){
                this.$emit('select', button.product);
                this.calculateCounts(button.product_id)
                return;
            }

            this.activeCategory = button.category;
            this.genProductButtons()
            this.calculateCounts()
        },

        calculateCounts(productId = null) {
            const productButtons = this.gridButtons.products.flat().filter(btn => !!btn);

            if(productId) {
                const button = productButtons.find(btn => btn.product_id === productId)
                if(button) {
                    button.selectedCount = this.curOrder
                        ? this.curOrder.items.filter(item => item.product_id === button.product_id)
                            .reduce((acc, cur) => (acc = acc + cur.getCount()), 0)
                        : 0
                }
                return;
            }

            productButtons.forEach(button => {
                button.selectedCount = this.curOrder
                    ? this.curOrder.items.filter(item => item.product_id === button.product_id)
                        .reduce((acc, cur) => (acc = acc + cur.getCount()), 0)
                    : 0
            })
        },

        resetCounts() {
            const productButtons = this.gridButtons.products.flat().filter(btn => !!btn);
            productButtons.forEach(button => {
                button.selectedCount = 0
            })
        }
    }
};
</script>

<style lang="scss" scoped>
.product-type-buttons {
    &.disabled {
        opacity: 0.5;
        pointer-events: none;
    }

    height: 100%;
    display: flex;
    flex-direction: column;

    .category-container {
        position: relative;
        flex: 1;

        table {
            width: 100%;
            height: 100%;
            table-layout: fixed;
            border-spacing: 7px;
            border-collapse: separate;
        }
    }

    .product-container {
        position: relative;
        flex: 3;

        table {
            width: 100%;
            height: 100%;
            table-layout: fixed;
            border-spacing: 7px;
            border-collapse: separate;
        }
    }

    .grid-settings {
        position: absolute;
        top: -2px;
        right: 0;
        z-index: 5;
        background: #29333fd1;
        border-radius: 2px;
        color: #f3f3f4;

        .row-column {
            display: flex;
            justify-content: space-between;

            .grid-btn {
                width: 25px;
                height: 25px;
                padding: 0;
                margin: 0;
            }

            .label {
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 12px;
            }
        }
    }
}
</style>
